/** DPR-aware crisp rendering utilities: text, rectangles, circles, graphics, and buttons for HiDPI displays. */
import Phaser from 'phaser';
import { DPR, CANVAS_SCALE, DPR_SCROLL_X, DPR_SCROLL_Y } from '../utils/Constants';
import { TAU } from '../utils/Math';
import { Font, applyButtonHover } from './Theme';

/**
 * Crisp rendering utilities for Phaser 3.
 *
 * With DPR-aware canvas (canvas at GAME_WIDTH*DPR × GAME_HEIGHT*DPR, camera
 * zoom = DPR), Phaser still renders text at the specified world-space font size.
 * The camera zoom scales the text texture up to fill the higher-res canvas.
 * CRISP_SCALE ensures text is rendered at least at the canvas's native pixel
 * resolution, so no texture upscaling occurs.
 *
 * On DPR=2: CRISP_SCALE=2, text rendered at 2x, camera zoom halves it back.
 * Result: text pixels map 1:1 to canvas pixels to physical display pixels.
 */

export const CRISP_SCALE = Math.max(2, Math.ceil(DPR));

/**
 * Apply DPR-aware camera setup for static (non-GameScene) scenes.
 * Sets zoom to DPR and applies scroll compensation so worldView starts at (0,0).
 */
export function setupCrispCamera(scene: Phaser.Scene): void {
  scene.cameras.main.setZoom(DPR * CANVAS_SCALE);
  scene.cameras.main.scrollX = DPR_SCROLL_X;
  scene.cameras.main.scrollY = DPR_SCROLL_Y;
}

const INV_CRISP = 1 / CRISP_SCALE;

/**
 * Create sharp text by rendering at 2x font size and displaying at 0.5 scale.
 * Pass the logical fontSize you want (e.g. '24px') — it renders at 48px internally.
 * Returns a standard Text object; all chaining (.setOrigin, .setInteractive, etc.) works.
 */
export function crispText(
  scene: Phaser.Scene,
  x: number,
  y: number,
  text: string | string[],
  style?: Phaser.Types.GameObjects.Text.TextStyle,
): Phaser.GameObjects.Text {
  const doubled = { ...style };
  if (doubled.fontSize) {
    const size = parseInt(doubled.fontSize as string, 10);
    const unit = (doubled.fontSize as string).replace(/[\d.]+/, '');
    doubled.fontSize = `${size * CRISP_SCALE}${unit}`;
  }
  // Scale stroke and shadow to match the 2x internal render size.
  // Without this, a strokeThickness of 3 would display as 1.5px.
  if (doubled.strokeThickness) {
    doubled.strokeThickness = doubled.strokeThickness * CRISP_SCALE;
  }
  if (doubled.shadow) {
    doubled.shadow = { ...doubled.shadow };
    if (doubled.shadow.blur) doubled.shadow.blur *= CRISP_SCALE;
    if (doubled.shadow.offsetX) doubled.shadow.offsetX *= CRISP_SCALE;
    if (doubled.shadow.offsetY) doubled.shadow.offsetY *= CRISP_SCALE;
  }
  const obj = scene.add.text(x, y, text, doubled).setScale(INV_CRISP);
  // Override pixelArt's NEAREST default so text stays anti-aliased
  obj.texture.setFilter(Phaser.Textures.FilterMode.LINEAR);
  return obj;
}

/**
 * Create a sharp rectangle by rendering at 2x dimensions and displaying at 0.5 scale.
 * Pass the logical width/height you want — visual size matches what you pass in.
 */
export function crispRect(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  color?: number,
  alpha?: number,
): Phaser.GameObjects.Rectangle {
  return scene.add
    .rectangle(x, y, width * CRISP_SCALE, height * CRISP_SCALE, color, alpha)
    .setScale(INV_CRISP);
}

/**
 * Create a sharp rounded rectangle using a canvas-rendered texture.
 * Like crispRect but with rounded corners. Position is center-origin.
 * Static — doesn't need per-frame redraw. Tint via setTint(), alpha via setAlpha().
 */
export function crispRoundedRect(
  scene: Phaser.Scene,
  x: number,
  y: number,
  width: number,
  height: number,
  radius: number,
  color?: number,
  alpha?: number,
): Phaser.GameObjects.Image {
  const w = Math.ceil(width * CRISP_SCALE);
  const h = Math.ceil(height * CRISP_SCALE);
  const r = Math.min(radius * CRISP_SCALE, w / 2, h / 2);
  const key = `_rrect-${w}x${h}r${Math.round(r)}`;

  if (!scene.textures.exists(key)) {
    const canvas = document.createElement('canvas');
    canvas.width = w;
    canvas.height = h;
    const ctx = canvas.getContext('2d')!;
    ctx.fillStyle = '#ffffff';
    ctx.beginPath();
    ctx.moveTo(r, 0);
    ctx.lineTo(w - r, 0);
    ctx.quadraticCurveTo(w, 0, w, r);
    ctx.lineTo(w, h - r);
    ctx.quadraticCurveTo(w, h, w - r, h);
    ctx.lineTo(r, h);
    ctx.quadraticCurveTo(0, h, 0, h - r);
    ctx.lineTo(0, r);
    ctx.quadraticCurveTo(0, 0, r, 0);
    ctx.closePath();
    ctx.fill();
    scene.textures.addCanvas(key, canvas);
    scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  }

  const img = scene.add.image(x, y, key).setScale(INV_CRISP);
  if (color !== undefined) img.setTint(color);
  if (alpha !== undefined) img.setAlpha(alpha);
  return img;
}

/**
 * Create a sharp Graphics object at 0.5 scale.
 * Callers must multiply all draw coordinates and sizes by CRISP_SCALE.
 */
export function crispGraphics(
  scene: Phaser.Scene,
  options?: Phaser.Types.GameObjects.Graphics.Options,
): Phaser.GameObjects.Graphics {
  return scene.add.graphics(options).setScale(INV_CRISP);
}

/**
 * Create a standard centered button with hover colors and click handler.
 * Handles crispText + setOrigin(0.5) + setInteractive + hover + pointerdown.
 */
export function crispButton(
  scene: Phaser.Scene,
  x: number,
  y: number,
  label: string,
  fontSize: string,
  colors: { default: string; hover: string },
  onClick: () => void,
): Phaser.GameObjects.Text {
  const btn = crispText(scene, x, y, label, {
    fontFamily: Font.family, fontSize, color: colors.default,
  }).setOrigin(0.5).setInteractive({ useHandCursor: true });
  applyButtonHover(btn, colors);
  btn.on('pointerdown', onClick);
  return btn;
}

/**
 * Ensure a smooth filled-circle texture exists at the given radius.
 * Uses Canvas 2D rendering (which has native anti-aliasing regardless of WebGL pixelArt state)
 * and LINEAR filtering for smooth edges at any display size.
 */
export function ensureCircleTexture(scene: Phaser.Scene, radius: number): string {
  const key = `_circle-r${radius}`;
  if (scene.textures.exists(key)) return key;
  const size = radius * 2 + 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.fillStyle = '#ffffff';
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, radius, 0, TAU);
  ctx.fill();
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/**
 * Ensure a soft-edged circle texture with radial gradient (bright center → transparent edge).
 * Uses Canvas 2D for native AA. Suitable for glow/aura effects.
 */
export function ensureSoftCircleTexture(scene: Phaser.Scene, radius: number): string {
  const key = `_soft-circle-r${radius}`;
  if (scene.textures.exists(key)) return key;
  const size = radius * 2 + 2;
  const cx = size / 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(cx, cx, 0, cx, cx, radius);
  gradient.addColorStop(0, 'rgba(220,235,255,1)');
  gradient.addColorStop(0.5, 'rgba(200,225,255,0.75)');
  gradient.addColorStop(0.8, 'rgba(180,215,255,0.3)');
  gradient.addColorStop(1, 'rgba(160,200,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/**
 * Ensure a soft glow texture with a tight bright core and a long gradual fade.
 * Uses Canvas 2D for native AA. Suitable for underlay glow effects behind icons.
 */
export function ensureGlowTexture(scene: Phaser.Scene, radius: number): string {
  const key = `_glow-r${radius}`;
  if (scene.textures.exists(key)) return key;
  const size = radius * 2 + 2;
  const cx = size / 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const gradient = ctx.createRadialGradient(cx, cx, 0, cx, cx, radius);
  gradient.addColorStop(0, 'rgba(255,255,255,1)');
  gradient.addColorStop(0.3, 'rgba(255,255,255,0.8)');
  gradient.addColorStop(0.5, 'rgba(255,255,255,0.4)');
  gradient.addColorStop(0.8, 'rgba(255,255,255,0.1)');
  gradient.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = gradient;
  ctx.fillRect(0, 0, size, size);
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/**
 * Ensure a smooth ring (stroke-only circle) texture exists at the given radius and line width.
 * Uses Canvas 2D rendering for native anti-aliasing.
 */
export function ensureRingTexture(scene: Phaser.Scene, radius: number, lineWidth: number): string {
  const key = `_ring-r${radius}-w${lineWidth}`;
  if (scene.textures.exists(key)) return key;
  const size = (radius + lineWidth) * 2 + 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  ctx.strokeStyle = '#ffffff';
  ctx.lineWidth = lineWidth;
  ctx.beginPath();
  ctx.arc(size / 2, size / 2, radius, 0, TAU);
  ctx.stroke();
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/**
 * Ensure a diffraction-spike texture exists — a thin vertical sliver, brightest
 * at center, fading to transparent at both tips. Callers rotate and scale
 * individual images to get spikes at arbitrary angles and lengths.
 */
export function ensureSpikeTexture(scene: Phaser.Scene, width: number, height: number): string {
  const key = `_spike-${width}x${height}`;
  if (scene.textures.exists(key)) return key;
  const canvas = document.createElement('canvas');
  canvas.width = width;
  canvas.height = height;
  const ctx = canvas.getContext('2d')!;

  // Vertical gradient: transparent → white at center → transparent
  const grad = ctx.createLinearGradient(0, 0, 0, height);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.35, 'rgba(255,255,255,0.6)');
  grad.addColorStop(0.5, 'rgba(255,255,255,1)');
  grad.addColorStop(0.65, 'rgba(255,255,255,0.6)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;

  // Soft horizontal edges via a rounded rect (pill shape)
  const r = width / 2;
  ctx.beginPath();
  ctx.moveTo(r, 0);
  ctx.lineTo(width - r, 0);
  ctx.quadraticCurveTo(width, 0, width, r);
  ctx.lineTo(width, height - r);
  ctx.quadraticCurveTo(width, height, width - r, height);
  ctx.lineTo(r, height);
  ctx.quadraticCurveTo(0, height, 0, height - r);
  ctx.lineTo(0, r);
  ctx.quadraticCurveTo(0, 0, r, 0);
  ctx.closePath();
  ctx.fill();

  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/** Canvas-rendered chromatic aberration texture: three RGB circles offset for a glitch/split effect. */
export function ensureChromaticTexture(
  scene: Phaser.Scene,
  radius: number,
  offset: number,
): string {
  const key = `_chromatic-${radius}-${offset}`;
  if (scene.textures.exists(key)) return key;
  const pad = offset * CRISP_SCALE;
  const size = radius * 2 * CRISP_SCALE + pad * 2;
  const canvas = document.createElement('canvas');
  canvas.width = size;
  canvas.height = size;
  const ctx = canvas.getContext('2d')!;
  const cx = size / 2;
  const cy = size / 2;
  const r = radius * CRISP_SCALE;
  const o = offset * CRISP_SCALE;
  ctx.globalCompositeOperation = 'lighter';
  ctx.fillStyle = 'rgba(255,0,0,0.6)';
  ctx.beginPath();
  ctx.arc(cx - o, cy, r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = 'rgba(0,255,0,0.6)';
  ctx.beginPath();
  ctx.arc(cx + o, cy, r, 0, TAU);
  ctx.fill();
  ctx.fillStyle = 'rgba(0,0,255,0.6)';
  ctx.beginPath();
  ctx.arc(cx, cy - o, r, 0, TAU);
  ctx.fill();
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/** Full-screen radial gradient texture: transparent center, opaque edges. Used for vignette effects. */
export function ensureVignetteTexture(
  scene: Phaser.Scene,
  width: number,
  height: number,
): string {
  const key = `_vignette-${width}x${height}`;
  if (scene.textures.exists(key)) return key;
  const w = width * CRISP_SCALE;
  const h = height * CRISP_SCALE;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  const cx = w / 2;
  const cy = h / 2;
  const r = Math.max(cx, cy);
  const grad = ctx.createRadialGradient(cx, cy, r * 0.4, cx, cy, r);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(1, 'rgba(255,255,255,1)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/** Horizontal beam texture: brightest at center row, fading to transparent at edges. */
export function ensureBeamTexture(
  scene: Phaser.Scene,
  width: number,
  height: number,
): string {
  const key = `_beam-${width}x${height}`;
  if (scene.textures.exists(key)) return key;
  const w = width * CRISP_SCALE;
  const h = height * CRISP_SCALE;
  const canvas = document.createElement('canvas');
  canvas.width = w;
  canvas.height = h;
  const ctx = canvas.getContext('2d')!;
  // Vertical gradient: transparent edges → bright core
  const grad = ctx.createLinearGradient(0, 0, 0, h);
  grad.addColorStop(0, 'rgba(255,255,255,0)');
  grad.addColorStop(0.3, 'rgba(255,255,255,0.6)');
  grad.addColorStop(0.5, 'rgba(255,255,255,1)');
  grad.addColorStop(0.7, 'rgba(255,255,255,0.6)');
  grad.addColorStop(1, 'rgba(255,255,255,0)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, w, h);
  scene.textures.addCanvas(key, canvas);
  scene.textures.get(key).setFilter(Phaser.Textures.FilterMode.LINEAR);
  return key;
}

/**
 * Create a smooth anti-aliased circle using a canvas-rendered texture.
 * Unlike WebGL Arc objects, canvas-rendered circles have native anti-aliasing
 * even when pixelArt: true disables WebGL MSAA.
 */
export function crispCircle(
  scene: Phaser.Scene,
  x: number,
  y: number,
  radius: number,
  color?: number,
  alpha?: number,
): Phaser.GameObjects.Image {
  const texKey = ensureCircleTexture(scene, radius * CRISP_SCALE);
  const img = scene.add.image(x, y, texKey).setScale(INV_CRISP);
  if (color !== undefined) img.setTint(color);
  if (alpha !== undefined) img.setAlpha(alpha);
  return img;
}
